/**
 * LgpdSection — LGPD Art. 18 data subject rights surface in /perfil.
 *
 * Two actions:
 *  - Exportar meus dados → calls /functions/v1/export-user-data, downloads JSON.
 *  - Excluir minha conta → confirm modal (type "EXCLUIR") → calls
 *    /functions/v1/delete-user-account, signs out.
 *
 * Sprint 0 Tier 1 Sec3.
 */
import { useState } from "react";
import { useClerk, useSession } from "@clerk/clerk-react";
import { DownloadSimple, Trash, Shield, Warning } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { loadEnv } from "@/lib/env";
import { useSupabaseClient } from "@/lib/supabase";
import { toast } from "@/lib/toast";

export function LgpdSection() {
  const { session } = useSession();
  // Resolve/create the profile before calling rights endpoints directly.
  const { isReady } = useSupabaseClient();
  const { signOut } = useClerk();
  const [exporting, setExporting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [confirmation, setConfirmation] = useState("");
  const [deleting, setDeleting] = useState(false);

  const supabaseUrl = (() => {
    try {
      return loadEnv().VITE_SUPABASE_URL ?? "";
    } catch {
      return "";
    }
  })();

  const handleExport = async () => {
    if (!session || !supabaseUrl) {
      toast.error("Sessão não pronta. Recarregue a página.");
      return;
    }
    setExporting(true);
    try {
      // These rights endpoints verify Clerk tokens, not the Supabase bridge.
      const token = await session.getToken();
      if (!token) throw new Error("Sessão não pronta. Entre novamente.");
      const resp = await fetch(
        `${supabaseUrl.replace(/\/$/, "")}/functions/v1/export-user-data`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${resp.status}`);
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `meus-dados-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Seus dados foram baixados.");
    } catch (err) {
      toast.error(
        `Erro ao exportar: ${err instanceof Error ? err.message : "tente novamente"}`,
      );
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    if (!session || !supabaseUrl) {
      toast.error("Sessão não pronta. Recarregue a página.");
      return;
    }
    if (confirmation !== "EXCLUIR") return;
    setDeleting(true);
    try {
      const token = await session.getToken();
      if (!token) throw new Error("Sessão não pronta. Entre novamente.");
      const resp = await fetch(
        `${supabaseUrl.replace(/\/$/, "")}/functions/v1/delete-user-account`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ confirmation: "EXCLUIR" }),
        },
      );
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${resp.status}`);
      }
      toast.success("Conta excluída. Saindo...");
      // Sign out (and force a full reload to clear any in-memory state).
      await signOut({ redirectUrl: "/login" });
    } catch (err) {
      toast.error(
        `Erro ao excluir: ${err instanceof Error ? err.message : "tente novamente"}`,
      );
      setDeleting(false);
    }
  };

  return (
    <div className="bg-card border border-border/50 rounded-xl p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded-lg bg-primary/10">
          <Shield weight="duotone" className="w-5 h-5 text-primary" aria-hidden="true" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground">Meus dados (LGPD Art. 18)</h2>
          <p className="text-sm text-muted-foreground">
            Exporte ou exclua tudo que você cadastrou no app.
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-foreground">Exportar meus dados</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Baixa um JSON com perfil, consultas, roteiros, criativos, simulações e contexto.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => void handleExport()}
            disabled={exporting || !session || !isReady}
            className="gap-2"
          >
            <DownloadSimple weight="bold" className="w-4 h-4" aria-hidden="true" />
            {exporting ? "Exportando..." : "Exportar"}
          </Button>
        </div>

        <div className="border-t border-border/40 pt-4 flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-destructive">Excluir minha conta</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Ação permanente. Apaga todos os seus dados e desconecta sua sessão.
            </p>
          </div>
          <Button
            variant="destructive"
            onClick={() => setDeleteOpen(true)}
            disabled={!session || !isReady}
            className="gap-2"
          >
            <Trash weight="bold" className="w-4 h-4" aria-hidden="true" />
            Excluir
          </Button>
        </div>
      </div>

      <Dialog open={deleteOpen} onOpenChange={(o) => !deleting && setDeleteOpen(o)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Warning weight="duotone" className="w-5 h-5" aria-hidden="true" />
              Excluir conta permanentemente
            </DialogTitle>
            <DialogDescription>
              Esta ação não pode ser desfeita. Todos os seus dados (perfil, consultas, roteiros,
              criativos, simulações, configurações) serão apagados.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="confirm-delete">
              Para confirmar, digite{" "}
              <span className="font-mono text-destructive">EXCLUIR</span> abaixo:
            </Label>
            <Input
              id="confirm-delete"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              placeholder="EXCLUIR"
              autoComplete="off"
              disabled={deleting}
            />
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteOpen(false);
                setConfirmation("");
              }}
              disabled={deleting}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleDelete()}
              disabled={confirmation !== "EXCLUIR" || deleting}
            >
              {deleting ? "Excluindo..." : "Excluir definitivamente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
